import { createMachine } from 'xstate'
import { createRpActions, createRpGuards } from './actions'
import { ERPlayState, gamePlayStates } from './GamePlay'
import { createRpServices } from './services'
import {
  CreateRussianPokerMachineParams,
  ERpAction,
  ERpEvent,
  ERpLeavingState,
  ERpService,
  ERpState,
  RussianPokerMachineContext,
  RussianPokerMachineEvent,
} from './types'

export * from './GamePlay'
export * from './selectors'
export * from './types'

export const createRussianPokerMachine = ({
  gameId,
  getRuntime,
}: CreateRussianPokerMachineParams) => {
  return createMachine<RussianPokerMachineContext, RussianPokerMachineEvent>(
    {
      id: 'russianPoker',
      initial: ERpState.Disconnected,
      predictableActionArguments: true,
      context: {
        gameId,
        sessionId: null,
        errorMessage: null,
        boxOnTurn: null,
        cardQueue: [],
        deferredPhaseAfterDealing: null,
        deferredSessionPatches: [],
        deferredBoxOnTurn: null,
        deferredBoxPatchesBySeat: {},
      },
      states: {
        [ERpState.Disconnected]: {
          on: {
            [ERpEvent.SocketConnected]: {
              target: ERpState.Connected,
              actions: ERpAction.ClearError,
            },
            [ERpEvent.Leave]: ERpState.Left,
            [ERpEvent.ForceLeave]: `${ERpState.Leaving}.${ERpLeavingState.Force}`,
          },
        },

        [ERpState.Connected]: {
          on: {
            [ERpEvent.SocketDisconnected]: ERpState.Disconnected,
            [ERpEvent.Join]: {
              target: ERpState.Joining,
              actions: ERpAction.ClearError,
            },
            [ERpEvent.Leave]: ERpState.Left,
            [ERpEvent.ForceLeave]: `${ERpState.Leaving}.${ERpLeavingState.Force}`,
            [ERpEvent.ServerLeaveGame]: {
              target: ERpState.Left,
              actions: ERpAction.NavigateLobby,
            },
          },
        },

        [ERpState.Joining]: {
          invoke: {
            id: ERpService.JoinGameRequest,
            src: ERpService.JoinGameRequest,
          },
          on: {
            [ERpEvent.SocketDisconnected]: ERpState.Disconnected,
            [ERpEvent.JoinSuccess]: {
              target: ERpState.Joined,
              actions: [
                ERpAction.PatchStoreFromJoinResult,
                ERpAction.SetSessionId,
                ERpAction.ClearError,
              ],
            },
            [ERpEvent.JoinFail]: {
              target: ERpState.Error,
              actions: [
                ERpAction.SetJoinError,
                ERpAction.ShowJoinFailToast,
                ERpAction.NavigateLobby,
              ],
            },
            [ERpEvent.Leave]: ERpState.Leaving,
            [ERpEvent.ForceLeave]: `${ERpState.Leaving}.${ERpLeavingState.Force}`,
            [ERpEvent.ServerLeaveGame]: {
              target: ERpState.Left,
              actions: ERpAction.NavigateLobby,
            },
          },
        },

        [ERpState.Joined]: {
          // syncing: reads store snapshot and routes to the correct play state
          initial: ERPlayState.Syncing,
          invoke: {
            src: ERpService.ListenGameEvents,
          },
          on: {
            // Connection-level events
            [ERpEvent.SocketDisconnected]: ERpState.Disconnected,
            [ERpEvent.Leave]: ERpState.Leaving,
            [ERpEvent.ForceLeave]: `${ERpState.Leaving}.${ERpLeavingState.Force}`,
            [ERpEvent.ServerLeaveGame]: {
              target: ERpState.Left,
              actions: ERpAction.NavigateLobby,
            },
            // Game-level events — valid in any joined substate
            [ERpEvent.GamePatchReceived]: { actions: ERpAction.ApplyGamePatch },
            [ERpEvent.DepositReceived]: { actions: ERpAction.ApplyDeposit },
          },
          states: gamePlayStates,
        },

        [ERpState.Leaving]: {
          initial: ERpLeavingState.Normal,
          states: {
            [ERpLeavingState.Normal]: {
              entry: ERpAction.EmitLeaveGame,
              always: `#russianPoker.${ERpState.Left}`,
            },
            [ERpLeavingState.Force]: {
              entry: [ERpAction.EmitForceLeaveGame, ERpAction.NavigateLobby],
              always: `#russianPoker.${ERpState.Left}`,
            },
          },
        },

        [ERpState.Left]: {
          on: {
            [ERpEvent.SocketDisconnected]: ERpState.Disconnected,
            [ERpEvent.SocketConnected]: ERpState.Connected,
            [ERpEvent.Join]: ERpState.Joining,
            [ERpEvent.ForceLeave]: `${ERpState.Leaving}.${ERpLeavingState.Force}`,
          },
        },

        [ERpState.Error]: {
          on: {
            [ERpEvent.SocketDisconnected]: ERpState.Disconnected,
            [ERpEvent.SocketConnected]: {
              target: ERpState.Connected,
              actions: ERpAction.ClearError,
            },
            [ERpEvent.Join]: {
              target: ERpState.Joining,
              actions: ERpAction.ClearError,
            },
            [ERpEvent.Leave]: ERpState.Left,
            [ERpEvent.ForceLeave]: `${ERpState.Leaving}.${ERpLeavingState.Force}`,
            [ERpEvent.ServerLeaveGame]: {
              target: ERpState.Left,
              actions: ERpAction.NavigateLobby,
            },
          },
        },
      },
    },
    {
      actions: createRpActions(getRuntime),
      services: createRpServices(getRuntime),
      guards: createRpGuards(getRuntime),
    }
  )
}
